































import Vue from "vue";
import Component from "vue-class-component";
import { CrudService } from "../core/services/crudService";
import { ShopService } from "../core/services/shopService";
import { Settings, SettingsService } from "../core/services/configService";
import { CustomerHelper } from "../crm/helpers/customerHelper";
import { ProgramPackTexts } from "./programPackTexts";
import ProgramPage from "../components/programs/ProgramPage.vue";
import ProgramMeta from "../components/programs/ProgramMeta.vue";
import ProgramPanel from "../components/programs/ProgramPanel.vue";
import ProgramColumns from "../components/programs/ProgramColumns.vue";
import ProgramReports from "../components/programs/ProgramReports.vue";
import ProgramEvolution from "../components/programs/ProgramEvolution.vue";
import CzCard from "../components/cz/CzCard.vue";
import CzChip from "../components/cz/CzChip.vue";
import CzTitle from "../components/cz/CzTitle.vue";
import CzRing from "../components/cz/CzRing.vue";
import CzSegments from "../components/cz/CzSegments.vue";
import CzSegmented from "../components/cz/CzSegmented.vue";
import CzNotice from "../components/cz/CzNotice.vue";
import CzButton from "../components/cz/CzButton.vue";
import { GuidelineApi, CardGuideline, ScopeProgram } from "../components/guidelines/guidelineApi";

@Component({
  components: { ProgramPage, ProgramMeta, ProgramPanel, ProgramColumns, ProgramReports, ProgramEvolution, CzCard, CzChip, CzTitle, CzRing, CzSegments, CzSegmented, CzNotice, CzButton },
})
export default class MyProgramPackPage extends Vue {
  enrollment: any = null;
  reports: any[] = [];
  loaded: boolean = false;
  tab: string = "plan";
  hasEvolution: boolean = false;
  errorMsg: string = "";
  texts = ProgramPackTexts;
  guidelines: { [id: number]: any } = {};
  today: string = "";
  busyId: number = 0;

  get emptyText(): string {
    return this.loaded && this.enrollment === null ? this.$t("This program is not available").toString() : "";
  }

  get tabs(): any[] {
    return [
      { value: "plan", label: this.t("Plan"), show: true },
      { value: "payments", label: this.t("Payments"), show: !!(this.enrollment.payment && this.enrollment.payment.installments.length) },
      { value: "evolution", label: this.t("Your evolution"), show: this.hasEvolution },
      { value: "reports", label: this.t("Reports"), show: this.reports.length > 0 },
    ].filter((tab) => tab.show);
  }

  get done(): number {
    return ProgramPackTexts.done(this.enrollment);
  }

  get sessions(): number {
    return ProgramPackTexts.sessions(this.enrollment);
  }

  get byPhases(): boolean {
    return this.enrollment.layout !== 0;
  }

  // What the header measures (D38): phases in a program by phases, everything done in a program by days.
  get progress(): { value: number; max: number; label: string } {
    if (this.byPhases) {
      const current = this.enrollment.columns[this.currentPhase];
      const label = this.t("Phase {0} of {1}").replace("{0}", String(this.currentPhase + 1)).replace("{1}", String(this.enrollment.columns.length));
      return { value: this.currentPhase, max: this.enrollment.columns.length, label: current && current.name ? label + " · " + current.name : label };
    }
    return { value: this.done, max: this.sessions, label: this.t("{0} of {1} done").replace("{0}", String(this.done)).replace("{1}", String(this.sessions)) };
  }

  get terms(): string[] {
    return this.texts.enrollmentFacts(this.t, this.enrollment).slice(0, -1).concat(this.texts.cancellation(this.t, this.enrollment));
  }

  // A guideline is one more row of its phase; the one for the whole program is painted in every phase and marked once.
  get columns(): any[] {
    const wholeProgram = this.enrollment.columns.reduce((all: any[], col: any) =>
      all.concat(col.cards.filter((k: any) => k.cardType === CardGuideline && k.guidelineScope === ScopeProgram)), []);
    return this.enrollment.columns.map((col: any, i: number) => {
      const own = col.cards.filter((k: any) => k.cardType === CardGuideline && k.guidelineScope !== ScopeProgram);
      const cards = own.concat(wholeProgram);
      return {
        name: col.name,
        summary: this.phaseSummary(col, i),
        rows: col.cards
          .filter((k: any) => k.cardType === 0)
          .reduce((rows: any[], k: any) => rows.concat(ProgramPackTexts.sessionRows(this.t, this.enrollment, k)), [])
          .map((r: any) => Object.assign(r, { icon: "calendar" }))
          .concat(cards.filter((k: any) => !this.guidelines[k.customerGuidelineId]).map((k: any) => this.pendingGuideline(k))),
        guidelines: cards.filter((k: any) => this.guidelines[k.customerGuidelineId]).map((k: any) => ({
          row: this.guidelines[k.customerGuidelineId],
          to: `/guidelines/${k.customerGuidelineId}?back=/customer-programs/${this.enrollment.id}`,
        })),
      };
    });
  }

  // The phase in progress is the first one with appointments still to happen or an own guideline still active.
  get currentPhase(): number {
    const index = this.enrollment.columns.findIndex((col: any) => !col.cards.every((k: any) => this.cardDone(k)));
    return index < 0 ? this.enrollment.columns.length - 1 : index;
  }

  phaseSummary(col: any, i: number): string {
    if (i < this.currentPhase || col.cards.every((k: any) => this.cardDone(k))) return this.t("Completed");
    if (i > this.currentPhase) return this.t("Not started yet");
    return this.t("In progress");
  }

  cardDone(k: any): boolean {
    if (k.cardType === 0) return k.bookings.filter((b: any) => b.done).length >= Math.max(1, k.quantity);
    if (k.cardType !== CardGuideline || k.guidelineScope === ScopeProgram) return true;
    return !!k.customerGuidelineId && !this.guidelines[k.customerGuidelineId];
  }

  pendingGuideline(k: any): any {
    const detail = k.customerGuidelineId
      ? this.$t("finished").toString()
      : k.guidelineScope === ScopeProgram ? this.$t("starts with the program").toString() : this.$t("starts with its phase").toString();
    return { name: k.name, note: this.$t("Guideline").toString(), detail: detail, icon: k.customerGuidelineId ? "" : "lock" };
  }

  async loadGuidelines() {
    const data = await GuidelineApi.list();
    if (!data || !data.ok) return;
    this.today = GuidelineApi.day(data.today);
    const byId: { [id: number]: any } = {};
    data.data.forEach((row: any) => (byId[row.id] = row));
    this.guidelines = byId;
  }

  // A by-days program in progress with appointments still to book is booked whole on one day, as when it was bought.
  get canBookDay(): boolean {
    const e = this.enrollment;
    return e.layout === 0 && e.status === 2 && e.columns.some((col: any) => col.cards.some((k: any) => k.cardType === 0 && k.bookings.length < Math.max(1, k.quantity)));
  }

  get items(): string[] {
    return ProgramPackTexts.itemNames(this.enrollment);
  }

  get paymentSummary(): string {
    const p = this.enrollment.payment;
    return this.t("{0} paid of {1}").replace("{0}", ProgramPackTexts.money(p.totalPaid)).replace("{1}", ProgramPackTexts.money(p.totalWithDiscount));
  }

  // One row per installment; what is due can be paid from here (the checkout charges exactly that), the rest is
  // charged to the saved card on its day.
  get paymentColumns(): any[] {
    const p = this.enrollment.payment;
    return [{
      name: "",
      rows: p.installments.map((i: any) => ({
        name: this.t("Installment {0} of {1}").replace("{0}", String(i.number)).replace("{1}", String(p.installments.length)),
        note: ProgramPackTexts.money(i.amount),
        done: i.covered,
        detail: i.covered ? this.t("paid") : this.t(i.overdue ? "overdue since {0}" : "due on {0}").replace("{0}", ProgramPackTexts.date(i.dueDate)),
        action: i.due && p.dueNow > 0 ? { label: this.t("Pay"), pay: true } : null,
      })),
    }];
  }

  // The sale goes to the cart for what is due now, the amount the checkout will charge.
  pay() {
    const p = this.enrollment.payment;
    const sale = { id: p.saleId, title: this.enrollment.name, description: this.enrollment.name, totalWithDiscount: p.totalWithDiscount, totalPaid: p.totalPaid, pending: p.dueNow, customerId: Number(CustomerHelper.getStoredCustomer()) };
    new ShopService().addSaleToCart(sale, null);
    this.$router.push("/checkout");
  }

  t(key: string): string {
    return this.$t(key).toString();
  }

  // The same public cancellation as "Your appointments": the unit goes back to pending and the row offers "Book" again.
  async cancel(action: any) {
    this.errorMsg = "";
    const data = await new CrudService().httpPost(`${Settings.HostName}/api/publicbookings/cancel/${action.bookingId}/${CustomerHelper.getStoredCustomer()}`);
    if (!data || data.ok === false) {
      this.errorMsg = data && data.errorMsg ? data.errorMsg : this.$t("Crash").toString();
      return;
    }
    await this.load();
  }

  async load() {
    const url = `${Settings.HostName}/api/publicprogrampacks/customer/${CustomerHelper.getStoredCustomer()}/${this.$route.params.id}`;
    const data = await new CrudService().httpGet(url);
    this.enrollment = data && data.ok ? data.model : null;
    this.loaded = true;
  }

  async mounted() {
    await Promise.all([this.load(), this.loadGuidelines()]);
    if (this.enrollment) {
      new SettingsService().setPageTitle(this.enrollment.name);
      const sent = await new CrudService().httpGet(`${Settings.HostName}/api/publicreports/customer/${CustomerHelper.getStoredCustomer()}`);
      this.reports = sent && sent.ok ? sent.data.filter((r: any) => r.customerProgramPackId === this.enrollment.id) : [];
    }
  }
}
